import { useEffect, useMemo, useState } from "react";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import {
  fetchSuperAdminBillingRiskAlerts,
  updateSuperAdminBillingRiskAlert,
} from "../api/superadmin";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./ReportsSectionCompact.css";

const PAGE_SIZES = [25, 50, 100, 200];

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "escalated", label: "Escalated" },
  { value: "reviewed", label: "Reviewed" },
  { value: "dismissed", label: "Dismissed" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];

const RISK_OPTIONS = [
  { value: "all", label: "All" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

function formatDateTime(value) {
  if (!value) return "--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function SummaryCard({ label, value, tone = "slate" }) {
  const toneClass =
    tone === "rose"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : "border-slate-200 bg-slate-50 text-slate-900";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value ?? 0}</div>
    </div>
  );
}

export default function SuperAdminBillingRiskAlerts() {
  const { is_super_admin } = useAuth();
  const [filters, setFilters] = useState({
    status: "open",
    risk_level: "all",
    search: "",
    page: 1,
    page_size: 50,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const totalPages = data?.total_pages || 1;
  const items = data?.items || [];
  const queryFilters = useMemo(() => filters, [filters]);

  const loadData = async (nextFilters = queryFilters) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSuperAdminBillingRiskAlerts(nextFilters);
      setData(payload);
    } catch (err) {
      setError(err?.message || "Unable to load billing risk alerts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (is_super_admin) loadData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  };

  const runSearch = () => loadData(filters);

  const clearFilters = () => {
    const next = { status: "open", risk_level: "all", search: "", page: 1, page_size: 50 };
    setFilters(next);
    loadData(next);
  };

  const goToPage = (page) => {
    const safePage = Math.max(1, Math.min(Number(page || 1), totalPages));
    const next = { ...filters, page: safePage };
    setFilters(next);
    loadData(next);
  };

  const handleAction = async (alertId, action) => {
    const note = window.prompt("Add an internal note for this billing risk alert.", "");
    if (note === null) return;
    setActingId(alertId);
    setError("");
    setNotice("");
    try {
      await updateSuperAdminBillingRiskAlert(alertId, { action, note });
      setNotice("Billing risk alert updated.");
      await loadData(filters);
    } catch (err) {
      setError(err?.message || "Unable to update billing risk alert.");
    } finally {
      setActingId(null);
    }
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          <ReportPageHeader title="Billing Risk Alerts" closeTo="/superadmin" />
          <div className={`space-y-6 px-6 py-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Billing Risk Alerts"
              subtitle="Review salons with unusual active service staff changes around billing."
              sections={[
                {
                  id: "review",
                  title: "Review alerts",
                  description:
                    "Use this page to review staff-count changes that may affect subscription billing.",
                  anchorId: "billing-risk-alerts-table",
                  bullets: [
                    "Open alerts need Super Admin review.",
                    "High risk usually means a staff count dropped near billing or came back shortly after billing.",
                    "Use internal notes to record the decision before marking an alert reviewed or dismissed.",
                  ],
                },
              ]}
              troubleshooting={[
                {
                  title: "A salon says the change was valid",
                  body: "Add a note and mark the alert reviewed or dismissed. The billing-period count still protects the current cycle.",
                },
              ]}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {notice ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <SummaryCard label="Open alerts" value={data?.summary?.open} tone="amber" />
              <SummaryCard label="High risk" value={data?.summary?.high} tone="rose" />
              <SummaryCard label="Reviewed" value={data?.summary?.reviewed} />
              <SummaryCard label="Total" value={data?.summary?.total} />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <div>
                  <label className={LABEL_TEXT}>Status</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Risk</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.risk_level} onChange={(event) => updateFilter("risk_level", event.target.value)}>
                    {RISK_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL_TEXT}>Search</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.search} onChange={(event) => updateFilter("search", event.target.value)} placeholder="Salon, business, or alert text" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Rows</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.page_size} onChange={(event) => updateFilter("page_size", Number(event.target.value))}>
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className={BTN_PRIMARY} onClick={runSearch} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
                <button type="button" className={BTN_OUTLINE} onClick={clearFilters} disabled={loading}>
                  Clear
                </button>
              </div>
            </div>

            <div id="billing-risk-alerts-table" className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Detected</th>
                    <th className="px-3 py-2 text-left">Salon</th>
                    <th className="px-3 py-2 text-left">Risk</th>
                    <th className="px-3 py-2 text-left">Counts</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan="7">Loading billing risk alerts...</td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan="7">No billing risk alerts found.</td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id} className="align-top hover:bg-slate-50">
                        <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{formatDateTime(row.detected_at_utc)}</td>
                        <td className="px-3 py-3">
                          <div className="font-semibold text-slate-900">{row.salon_name || `Salon ${row.salon_id}`}</div>
                          <div className="text-xs text-slate-500">{row.business_name || `Business ${row.business_id}`}</div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            row.risk_level === "high" || row.risk_level === "critical"
                              ? "bg-rose-50 text-rose-700"
                              : "bg-amber-50 text-amber-700"
                          }`}>
                            {titleCase(row.risk_level)}
                          </span>
                          <div className="mt-1 text-xs text-slate-500">{titleCase(row.alert_type)}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <div>Current: <span className="font-semibold">{row.current_staff_count}</span></div>
                          <div>Billing period: <span className="font-semibold">{row.billing_period_staff_count}</span></div>
                          <div className="text-xs text-slate-500">
                            {formatDateTime(row.billing_period_start_utc)} to {formatDateTime(row.billing_period_end_utc)}
                          </div>
                        </td>
                        <td className="px-3 py-3 max-w-xl text-slate-700">
                          <div>{row.trigger_summary}</div>
                          {row.internal_notes ? (
                            <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                              {row.internal_notes}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-slate-700">{titleCase(row.status)}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button type="button" className={BTN_OUTLINE} disabled={actingId === row.id} onClick={() => handleAction(row.id, "mark_reviewed")}>Reviewed</button>
                            <button type="button" className={BTN_OUTLINE} disabled={actingId === row.id} onClick={() => handleAction(row.id, "escalate")}>Escalate</button>
                            <button type="button" className={BTN_OUTLINE} disabled={actingId === row.id} onClick={() => handleAction(row.id, "dismiss")}>Dismiss</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm">
              <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page - 1)} disabled={loading || filters.page <= 1}>
                Previous
              </button>
              <div className="text-slate-600">Page {filters.page} of {totalPages}</div>
              <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page + 1)} disabled={loading || filters.page >= totalPages}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
